// Regex patterns for alias validation
const VALID_CHARS_PATTERN = /^[a-zA-Z0-9._-]*$/
const INVALID_CHARS_PATTERN =
  /[\s\u00C0-\u024F!@#$%^&*()+=[\]{}|\\:;"'<>,?/`~\u{1F600}-\u{1F64F}\u{1F300}-\u{1F5FF}\u{1F680}-\u{1F6FF}\u{1F1E0}-\u{1F1FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/u

// Min and max length constants
export const ALIAS_MIN_LENGTH = 6
export const ALIAS_MAX_LENGTH = 20

/**
 * Validates if alias length is between 6 and 20 characters
 */
export const isLengthValid = (value: string): boolean => {
  const trimmed = value.trim()
  return trimmed.length >= ALIAS_MIN_LENGTH && trimmed.length <= ALIAS_MAX_LENGTH
}

/**
 * Validates if alias contains only allowed characters:
 * - Letters (a-z, A-Z)
 * - Numbers (0-9)
 * - Dots (.)
 * - Underscores (_)
 * - Hyphens (-)
 */
export const hasOnlyValidChars = (value: string): boolean => {
  const trimmed = value.trim()
  if (trimmed.length === 0) return false
  return VALID_CHARS_PATTERN.test(trimmed)
}

/**
 * Validates if alias does NOT contain invalid characters:
 * - Spaces
 * - Special characters: !, @, #, $, %, ^, &, *, (, ), +, =, {}, [], |, \, :, ;, ", ', <, >, ,, ?, /
 * - Accented characters: á, é, í, ó, ú, ç, ñ, Ñ, etc.
 * - Emojis or graphic symbols
 */
export const hasNoInvalidChars = (value: string): boolean => {
  const trimmed = value.trim()
  if (trimmed.length === 0) return true
  return !INVALID_CHARS_PATTERN.test(trimmed)
}

/**
 * Validates all alias rules at once
 */
export const validateAlias = (value: string) => ({
  lengthValid: isLengthValid(value),
  charsValid: hasOnlyValidChars(value),
  noInvalidChars: hasNoInvalidChars(value),
})

/**
 * Checks if all validations pass
 */
export const isAliasValid = (value: string): boolean => {
  const trimmed = value.trim()
  if (trimmed.length === 0) return false

  const validations = validateAlias(value)
  return validations.lengthValid && validations.charsValid && validations.noInvalidChars
}

export type AliasValidations = {
  lengthValid: boolean
  charsValid: boolean
  noInvalidChars: boolean
}
